import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { zampassRedirectLogin, completeRegistration } from "../services/api";
import { useAuth } from "../context/AuthContext";

// Lucide Icons
import { Eye, AlertCircle } from "lucide-react";

// Helper function to format labels correctly
const formatLabel = (field) => {
  const result = field.replace(/([A-Z])/g, " $1");
  return result.charAt(0).toUpperCase() + result.slice(1);
};

function ZampassSignInPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [registrationData, setRegistrationData] = useState({
    firstName: "",
    lastName: "",
    nationalId: "",
    email: "",
    phoneNumber: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [limitedToken, setLimitedToken] = useState(null);
  const [zampassid, setZampassId] = useState(null);
  const [zamPassIdTokenSet, setZampassIdToken] = useState(null);

  const { loginWithToken } = useAuth();
  const navigate = useNavigate();
  const processingRef = useRef(false);

  useEffect(() => {
    const handleCallback = async () => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get("code");
      const state = params.get("state");
      const site = "public";

      if (params.get("error")) {
        setError(`ZamPass Error: ${params.get("error_description") || params.get("error")}`);
        setLoading(false);
        return;
      }

      if (!code) {
        if (!processingRef.current) setError("No authorization code received from ZamPass.");
        setLoading(false);
        return;
      }

      if (processingRef.current) return;
      processingRef.current = true;

      try {
        const response = await zampassRedirectLogin(code, state, site);

        if (response.data.status === "unregistered") {
            console.log("unregister data", response.data)
          setLimitedToken(response.data.token);
          setZampassId(response.data.zampassId);
          setZampassIdToken(response.data.tokensFromZamPass.id_token);

          if (response.data.user?.email) {
            setRegistrationData((prev) => ({ ...prev, email: response.data.user.email }));
          }
          setShowRegistrationForm(true);
          setLoading(false);
          return;
        }

        const jwtToken = response.data.token;
        const userData = response.data.user;
        const zamPassIdToken = response.data.tokensFromZamPass.id_token;

        loginWithToken(jwtToken, userData, zamPassIdToken);
        navigate("/", { replace: true });
      } catch (err) {
        console.error("[ZamPass Login Failed]", err);
        setError("Failed to complete login with ZamPass.");
      } finally {
        setLoading(false);
      }
    };

    handleCallback();
  }, [loginWithToken, navigate]);

  const handleRegistrationSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    console.log("zp", zampassid);
    const payload = {
        ...registrationData,
        zampassid: zampassid,
    };

    try {
      const response = await completeRegistration(payload);

      const jwtToken = limitedToken;
      const userData = response.data.user;

      loginWithToken(jwtToken, userData, zamPassIdTokenSet);
      navigate("/", { replace: true });
    } catch (err) {
      console.error("[Registration Failed]", err);
      setError("Failed to complete registration.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setRegistrationData((prev) => ({ ...prev, [field]: value }));
  };

  // --- STYLES ---

  const containerStyle = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#f9fafb", // Light gray background
  };

  const formStyle = {
    backgroundColor: "#fff",
    border: "1px solid #e2e8f0", // Softer border
    borderRadius: "12px", // More rounded
    padding: "40px", // More padding
    width: "100%",
    maxWidth: "480px", // Wider form
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)", // Softer, larger shadow
  };

  const inputStyle = {
    width: "100%",
    padding: "12px 16px", // Bigger padding
    borderRadius: "8px", // Rounded inputs
    border: "1px solid #cbd5e1", // Slate-300 border
    marginTop: "6px", // Space from label
    marginBottom: "20px", // More space between fields
    fontSize: "16px", // Bigger font
    boxSizing: "border-box", // Ensure padding doesn't break width
  };

  const labelStyle = {
    fontSize: "16px", // Bigger font
    fontWeight: "600", // Bolder
    color: "#334155", // Darker text
  };

  const buttonStyle = {
    width: "100%",
    padding: "14px", // Taller button
    borderRadius: "8px",
    border: "none",
    backgroundColor: "#16a34a", // A nicer green
    color: "#fff",
    fontWeight: "600",
    cursor: "pointer",
    fontSize: "16px",
    marginTop: "16px", // Space above button
  };

  const errorStyle = {
    backgroundColor: "#fee2e2",
    color: "#b91c1c",
    padding: "12px 16px", // Bigger padding
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    gap: "8px", // More gap
    marginBottom: "16px",
    fontSize: "16px", // Bigger font
  };

  // --- RENDER ---

  if (showRegistrationForm) {
    return (
      <div style={containerStyle}>
        <div style={formStyle}>
          <h2 style={{ fontSize: "28px", fontWeight: "700", marginBottom: "12px", textAlign: "center", color: "#1e293b" }}>
            Complete Your Registration
          </h2>
          <p style={{ fontSize: "16px", color: "#64748b", marginBottom: "32px", textAlign: "center" }}>
            Please provide the following information to complete your account setup.
          </p>

          <form onSubmit={handleRegistrationSubmit}>
            {["firstName", "lastName", "nationalId", "email", "phoneNumber"].map((field) => (
              <div key={field}>
                <label style={labelStyle}>
                  {formatLabel(field)} *
                </label>
                <input
                  type={field === "email" ? "email" : "text"}
                  required
                  style={inputStyle}
                  value={registrationData[field]}
                  onChange={(e) => handleInputChange(field, e.target.value)}
                  placeholder={`Enter your ${formatLabel(field).toLowerCase()}`}
                />
              </div>
            ))}

            {error && (
              <div style={errorStyle}>
                <AlertCircle style={{ width: "20px", height: "20px", flexShrink: 0 }} />
                <span>{error}</span>
              </div>
            )}

            <button type="submit" style={buttonStyle} disabled={submitting}>
              {submitting ? "Submitting..." : "Complete Registration"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      {loading && <p style={{ color: "#4b5563", fontSize: "18px" }}>Processing ZamPass login...</p>}

      {error && (
        <div style={{ ...formStyle, maxWidth: "440px" }}> {/* Re-using formStyle for error card */}
          <h3 style={{ fontSize: "20px", fontWeight: "600", color: "#b91c1c", marginBottom: "12px", display: "flex", alignItems: "center", gap: "8px" }}>
            <AlertCircle style={{ width: "22px", height: "22px" }} /> Login Error
          </h3>
          <p style={{ fontSize: "16px", color: "#6b7280", marginBottom: "24px" }}>{error}</p>
          <button
            onClick={() => navigate("/login")}
            style={{ ...buttonStyle, backgroundColor: "#1e40af" }} // Blue button
          >
            Return to Login Page
          </button>
        </div>
      )}
    </div>
  );
}

export default ZampassSignInPage;